import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Upload, FileText, Image as ImageIcon, Archive, X, Check, Loader2,
  ChevronRight, ChevronLeft, Sparkles, Crown, PenTool, Eye,
  Trash2, Merge, ShoppingBag, ArrowLeft, CheckCircle2, AlertCircle,
  Package, GraduationCap, BookOpen
} from 'lucide-react';
import AddToCourseModal from '../components/AddToCourseModal';

const STEPS = ['Upload', 'Review', 'Convert', 'Processing', 'Complete'];

const BulkUpload = ({ user, embedded }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);

  const [step, setStep] = useState(0); // 0=Upload, 1=Review, 2=Convert, 3=Processing, 4=Complete
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [processingOcr, setProcessingOcr] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [isPro, setIsPro] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [dragActive, setDragActive] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [courseModal, setCourseModal] = useState(null);
  const [mergeMode, setMergeMode] = useState(false);
  const [mergeSelected, setMergeSelected] = useState([]);
  const [mergeTitle, setMergeTitle] = useState('');
  const [showMergeConfirm, setShowMergeConfirm] = useState(false);
  const [errorReason, setErrorReason] = useState(null);
  const pollingRef = useRef(null);

  // Handle payment return
  useEffect(() => {
    const payment = searchParams.get('payment');
    const bulkId = searchParams.get('bulk_id');
    if (payment === 'success' && bulkId) {
      setSessionId(bulkId);
      setStep(3);
      triggerGeneration(bulkId);
    }
  }, [searchParams]);

  const handleFiles = (fileList) => {
    const allowed = ['.pdf', '.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif', '.docx', '.txt', '.zip'];
    const newFiles = Array.from(fileList).filter(f => {
      if (!f.name || f.size === 0) return false;
      const ext = '.' + f.name.split('.').pop().toLowerCase();
      return allowed.includes(ext);
    });
    if (newFiles.length === 0) {
      toast.error('No supported files found. Use PDF, images, DOCX, TXT, or ZIP.');
      return;
    }
    const total = files.length + newFiles.length;
    if (total > 30) {
      toast.error('Maximum 30 files allowed');
      return;
    }
    setFiles(prev => [...prev, ...newFiles]);
    // Reset inputs so the same input can be used again
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (folderInputRef.current) folderInputRef.current.value = '';
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === 'dragenter' || e.type === 'dragover');
  };

  // Recursively read files from a directory entry
  const readDirectory = (dirEntry) => {
    return new Promise((resolve) => {
      const reader = dirEntry.createReader();
      const allFiles = [];
      const readBatch = () => {
        reader.readEntries((entries) => {
          if (entries.length === 0) {
            resolve(allFiles);
            return;
          }
          const promises = entries.map(entry => {
            if (entry.isFile) {
              return new Promise((res) => entry.file(f => res(f), () => res(null)));
            } else if (entry.isDirectory) {
              return readDirectory(entry);
            }
            return Promise.resolve(null);
          });
          Promise.all(promises).then(results => {
            results.flat().filter(Boolean).forEach(f => allFiles.push(f));
            readBatch();
          });
        }, () => resolve(allFiles));
      };
      readBatch();
    });
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const items = e.dataTransfer.items;
    if (!items || items.length === 0) {
      if (e.dataTransfer.files?.length) handleFiles(e.dataTransfer.files);
      return;
    }

    // Check if any dropped item is a directory
    const allFiles = [];
    const entries = [];
    for (let i = 0; i < items.length; i++) {
      const entry = items[i].webkitGetAsEntry?.();
      if (entry) entries.push(entry);
    }

    if (entries.length > 0) {
      for (const entry of entries) {
        if (entry.isDirectory) {
          const dirFiles = await readDirectory(entry);
          allFiles.push(...dirFiles);
        } else if (entry.isFile) {
          const file = await new Promise((res) => entry.file(f => res(f), () => res(null)));
          if (file) allFiles.push(file);
        }
      }
      if (allFiles.length > 0) {
        handleFiles(allFiles);
      } else {
        toast.error('No supported files found in the dropped folder');
      }
    } else {
      if (e.dataTransfer.files?.length) handleFiles(e.dataTransfer.files);
    }
  };

  const getFileIcon = (name) => {
    const ext = name.split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif'].includes(ext)) return <ImageIcon className="w-4 h-4 text-orange-500" />;
    if (ext === 'zip') return <Archive className="w-4 h-4 text-purple-500" />;
    return <FileText className="w-4 h-4 text-blue-500" />;
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  };

  // Step 1: Upload files
  const handleUpload = async () => {
    if (files.length === 0) return;
    setUploading(true);
    try {
      const formData = new FormData();
      files.forEach(f => formData.append('files', f));

      const res = await axios.post(`${API}/bulk-upload/create`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000
      });
      const sid = res.data.session_id;
      setSessionId(sid);
      toast.success(`${res.data.file_count} files uploaded!`);

      // If images need OCR, kick off background processing and poll
      if (res.data.images_pending_ocr > 0) {
        setProcessingOcr(true);
        toast.info(`Processing ${res.data.images_pending_ocr} images with AI OCR...`);
        try {
          await axios.post(`${API}/bulk-upload/${sid}/process-ocr`);

          // Poll for OCR completion (up to 3 minutes)
          let attempts = 0;
          while (attempts < 90) {
            await new Promise(r => setTimeout(r, 2000));
            try {
              const check = await axios.get(`${API}/bulk-upload/${sid}`);
              if (check.data.status === 'ready' || check.data.status === 'ocr_failed') {
                break;
              }
            } catch {
              // Polling error, continue trying
            }
            attempts++;
          }
        } catch (ocrErr) {
          console.error('OCR processing error:', ocrErr);
          toast.warning('OCR processing had issues — some documents may have limited text');
        }
        setProcessingOcr(false);
      }

      // Fetch session data (upload succeeded even if OCR had issues)
      try {
        const session = await axios.get(`${API}/bulk-upload/${sid}`);
        setSessionData(session.data);
        setDocuments(session.data.documents || []);
        setPricing(session.data.pricing);
        setIsPro(session.data.is_pro);
        if (session.data.error_reason) {
          setErrorReason(session.data.error_reason);
          toast.error(session.data.error_reason);
        }
        setStep(1);
      } catch {
        // If session fetch fails, still try to move forward
        toast.error('Could not load session data. Please try refreshing.');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed — check your connection and try again');
    } finally {
      setUploading(false);
      setProcessingOcr(false);
    }
  };

  // Refresh session data
  const refreshSession = async () => {
    if (!sessionId) return;
    const res = await axios.get(`${API}/bulk-upload/${sessionId}`);
    setSessionData(res.data);
    setDocuments(res.data.documents || []);
    setPricing(res.data.pricing);
    setIsPro(res.data.is_pro);
    if (res.data.error_reason) setErrorReason(res.data.error_reason);
  };

  // Toggle document selection
  const toggleDocSelection = (docIndex) => {
    setDocuments(prev => prev.map(d =>
      d.doc_index === docIndex ? { ...d, selected: !d.selected } : d
    ));
  };

  // Update document title
  const updateDocTitle = (docIndex, title) => {
    setDocuments(prev => prev.map(d =>
      d.doc_index === docIndex ? { ...d, title } : d
    ));
  };

  // Save document changes
  const saveDocuments = async () => {
    try {
      const formData = new FormData();
      formData.append('documents', JSON.stringify(documents));
      await axios.put(`${API}/bulk-upload/${sessionId}/documents`, formData);
    } catch (e) {
      console.error('Failed to save documents:', e);
    }
  };

  // Merge selected files (mergeSelected tracks doc_index values)
  const startMerge = () => {
    if (mergeSelected.length < 2) {
      toast.error('Select at least 2 documents to merge');
      return;
    }
    const names = documents
      .filter(d => mergeSelected.includes(d.doc_index))
      .map(d => d.title)
      .join(' + ');
    setMergeTitle(names);
    setShowMergeConfirm(true);
  };

  const handleMerge = async () => {
    if (!mergeTitle.trim()) {
      toast.error('Enter a name for the merged document');
      return;
    }
    setShowMergeConfirm(false);
    try {
      // Collect ALL file IDs from selected documents' source_files
      const selectedDocs = documents.filter(d => mergeSelected.includes(d.doc_index));
      const allFileIds = selectedDocs.flatMap(d => d.source_files || []);
      const formData = new FormData();
      formData.append('file_ids', JSON.stringify(allFileIds));
      formData.append('title', mergeTitle.trim());
      await axios.post(`${API}/bulk-upload/${sessionId}/merge`, formData);
      await refreshSession();
      setMergeMode(false);
      setMergeSelected([]);
      toast.success(`Merged ${selectedDocs.length} documents into "${mergeTitle.trim()}"`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Merge failed');
    }
  };

  const handleMergeAll = async () => {
    const allDocIndices = documents.map(d => d.doc_index);
    setMergeSelected(allDocIndices);
    const names = documents.map(d => d.title).join(' + ');
    setMergeTitle(names);
    setShowMergeConfirm(true);
  };

  // Proceed to payment/generation
  const handleConvert = async () => {
    await saveDocuments();
    const selected = documents.filter(d => d.selected);
    if (selected.length === 0) {
      toast.error('Select at least one document');
      return;
    }

    if (isPro) {
      // Pro: skip payment, generate directly
      setStep(3);
      triggerGeneration(sessionId);
      return;
    }

    // Create checkout
    try {
      const formData = new FormData();
      formData.append('origin_url', window.location.origin);
      const res = await axios.post(`${API}/bulk-upload/${sessionId}/checkout`, formData);
      if (res.data.already_subscribed) {
        setStep(3);
        triggerGeneration(sessionId);
      } else if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Checkout failed');
    }
  };

  // Trigger generation
  const triggerGeneration = async (sid) => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/bulk-upload/${sid}/generate`);
      setProgress({ current: 0, total: res.data.total });
      pollProgress(sid);
    } catch (e) {
      if (e.response?.status === 409) {
        // OCR still processing — wait and retry
        toast.info('OCR still processing, waiting...');
        await new Promise(r => setTimeout(r, 5000));
        return triggerGeneration(sid);
      }
      toast.error(e.response?.data?.detail || 'Generation failed');
      setGenerating(false);
    }
  };

  // Poll generation progress
  const pollProgress = useCallback((sid) => {
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/bulk-upload/${sid}/progress`);
        setProgress({ current: res.data.progress, total: res.data.total });
        setDocuments(res.data.documents || []);
        if (res.data.error_reason) setErrorReason(res.data.error_reason);

        if (res.data.status === 'completed') {
          setGenerating(false);
          setStep(4);
          return;
        }
        pollingRef.current = setTimeout(() => poll(), 3000);
      } catch {
        pollingRef.current = setTimeout(() => poll(), 5000);
      }
    };
    poll();
  }, []);

  useEffect(() => {
    return () => { if (pollingRef.current) clearTimeout(pollingRef.current); };
  }, []);

  // Recalculate pricing when selection changes — based on total SOURCE FILES, not document count
  useEffect(() => {
    const selected = documents.filter(d => d.selected);
    const totalSourceFiles = selected.reduce((acc, d) => acc + Math.max((d.source_files?.length || 0), 1), 0);
    if (totalSourceFiles > 0) {
      // Build per-file prices
      const perFilePrice = [];
      let total = 0;
      for (let i = 0; i < totalSourceFiles; i++) {
        const p = i === 0 ? 3 : i < 5 ? 2 : 1;
        perFilePrice.push(p);
        total += p;
      }
      // Compute per-document price (sum of source file prices within each doc)
      const perDocPrice = [];
      let fileIdx = 0;
      for (const d of selected) {
        const numFiles = Math.max((d.source_files?.length || 0), 1);
        const docPrice = perFilePrice.slice(fileIdx, fileIdx + numFiles).reduce((a, b) => a + b, 0);
        perDocPrice.push(docPrice);
        fileIdx += numFiles;
      }
      setPricing({
        total,
        per_doc: perDocPrice,
        per_file: perFilePrice,
        savings: totalSourceFiles * 3 - total,
        full_price: totalSourceFiles * 3,
        total_source_files: totalSourceFiles,
      });
    } else {
      setPricing({ total: 0, per_doc: [], per_file: [], savings: 0, full_price: 0, total_source_files: 0 });
    }
  }, [documents]);

  // --- RENDER ---
  return (
    <div className={embedded ? '' : 'min-h-screen bg-gradient-to-b from-gray-50 to-white'}>
      <div className={embedded ? '' : 'max-w-4xl mx-auto px-4 py-8'}>
        {/* Header - hidden when embedded */}
        {!embedded && (
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="rounded-xl">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-800" style={{ fontFamily: 'Crimson Text, serif' }}>
              Bulk Upload
            </h1>
            <p className="text-sm text-gray-500">Upload multiple notes and convert them to Study Dashboards</p>
          </div>
        </div>
        )}

        {/* Progress Steps */}
        <div className="flex items-center gap-1 mb-8 overflow-x-auto pb-2" data-testid="bulk-steps">
          {STEPS.map((s, i) => (
            <React.Fragment key={s}>
              <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${
                i < step ? 'bg-emerald-100 text-emerald-700'
                : i === step ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-400'
              }`}>
                {i < step ? <Check className="w-3 h-3" /> : <span>{i + 1}</span>}
                <span>{s}</span>
              </div>
              {i < STEPS.length - 1 && <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />}
            </React.Fragment>
          ))}
        </div>

        {/* Step 0: Upload */}
        {step === 0 && (
          <div className="space-y-4" data-testid="bulk-step-upload">
            <div
              data-testid="bulk-dropzone"
              className={`border-2 border-dashed rounded-2xl p-8 text-center transition-all cursor-pointer ${
                dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-white hover:border-blue-400'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                data-testid="bulk-file-input"
                type="file"
                className="hidden"
                multiple
                accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,.docx,.txt,.zip"
                onChange={(e) => { if (e.target.files?.length) handleFiles(e.target.files); }}
              />
              <input
                ref={folderInputRef}
                data-testid="bulk-folder-input"
                type="file"
                className="hidden"
                webkitdirectory=""
                directory=""
                multiple
                onChange={(e) => { if (e.target.files?.length) handleFiles(e.target.files); }}
              />
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mb-4">
                  <Package className="w-8 h-8 text-blue-600" />
                </div>
                <p className="text-lg font-medium text-gray-700 mb-1">Drop files or folders here</p>
                <p className="text-sm text-gray-500 mb-3">or use the buttons below</p>
                <div className="flex gap-3 mb-3">
                  <Button
                    data-testid="browse-files-btn"
                    variant="outline"
                    size="sm"
                    className="rounded-lg"
                    onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                  >
                    <Upload className="w-4 h-4 mr-1.5" /> Browse Files
                  </Button>
                  <Button
                    data-testid="browse-folder-btn"
                    variant="outline"
                    size="sm"
                    className="rounded-lg"
                    onClick={(e) => { e.stopPropagation(); folderInputRef.current?.click(); }}
                  >
                    <Archive className="w-4 h-4 mr-1.5" /> Upload Folder
                  </Button>
                </div>
                <div className="flex flex-wrap justify-center gap-2 text-xs text-gray-400">
                  <span className="flex items-center gap-1"><FileText className="w-3 h-3" /> PDFs</span>
                  <span className="flex items-center gap-1"><ImageIcon className="w-3 h-3" /> Images</span>
                  <span className="flex items-center gap-1"><Archive className="w-3 h-3" /> ZIP files</span>
                  <span className="flex items-center gap-1"><FileText className="w-3 h-3" /> DOCX/TXT</span>
                </div>
                <p className="text-xs text-gray-400 mt-2">Up to 30 files, 50MB each</p>
              </div>
            </div>

            {/* File List */}
            {files.length > 0 && (
              <div className="bg-white rounded-2xl border border-gray-200 p-4 space-y-2">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-gray-700">{files.length} file{files.length !== 1 ? 's' : ''} selected</p>
                  <Button variant="ghost" size="sm" onClick={() => setFiles([])} className="text-gray-400 hover:text-red-500 text-xs">
                    Clear all
                  </Button>
                </div>
                <div className="max-h-60 overflow-y-auto space-y-1">
                  {files.map((f, i) => (
                    <div key={i} className="flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-gray-50">
                      <div className="flex items-center gap-2 min-w-0">
                        {getFileIcon(f.name)}
                        <span className="text-sm text-gray-700 truncate">{f.name}</span>
                        <span className="text-xs text-gray-400 flex-shrink-0">{formatSize(f.size)}</span>
                      </div>
                      <button onClick={() => removeFile(i)} className="text-gray-400 hover:text-red-500 p-1">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
                <Button
                  data-testid="bulk-upload-btn"
                  onClick={handleUpload}
                  disabled={uploading || processingOcr}
                  className="w-full mt-3 rounded-xl bg-blue-600 hover:bg-blue-700"
                >
                  {uploading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading & Processing...</>
                  ) : processingOcr ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Running AI OCR on images...</>
                  ) : (
                    <><Upload className="w-4 h-4 mr-2" /> Upload {files.length} File{files.length !== 1 ? 's' : ''}</>
                  )}
                </Button>
              </div>
            )}

            <p className="text-xs text-gray-400 text-center">
              Upload is free. You only pay when converting to AI Study Dashboards.
            </p>
          </div>
        )}

        {/* Step 1: Review & Organize */}
        {step === 1 && (
          <div className="space-y-4" data-testid="bulk-step-review">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-800">We detected {documents.length} document{documents.length !== 1 ? 's' : ''}</h2>
                <p className="text-sm text-gray-500">Review, rename, or merge files before converting</p>
              </div>
              <div className="flex gap-2">
                {!mergeMode ? (
                  <div className="flex gap-2">
                    {documents.length >= 2 && (
                      <>
                        <Button data-testid="merge-all-btn" size="sm" variant="outline" onClick={handleMergeAll} className="rounded-lg text-xs border-purple-200 text-purple-600">
                          <Merge className="w-3.5 h-3.5 mr-1" /> Merge All into One
                        </Button>
                        <Button data-testid="merge-mode-btn" size="sm" variant="ghost" onClick={() => setMergeMode(true)} className="rounded-lg text-xs text-gray-500">
                          Select & Merge
                        </Button>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => { setMergeMode(false); setMergeSelected([]); }} className="rounded-lg text-xs">Cancel</Button>
                    <Button
                      data-testid="merge-execute-btn"
                      size="sm" onClick={startMerge} disabled={mergeSelected.length < 2}
                      className="rounded-lg text-xs bg-purple-600 hover:bg-purple-700"
                    >
                      <Merge className="w-3.5 h-3.5 mr-1" /> Merge {mergeSelected.length} Selected
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {mergeMode && (
              <div className="p-3 bg-purple-50 border border-purple-200 rounded-xl mb-2">
                <p className="text-sm text-purple-800">
                  Click on documents to select them, then click "Merge" to combine into one study dashboard. Pricing is still based on the original file count.
                </p>
              </div>
            )}

            <div className="space-y-3">
              {documents.map((doc, i) => {
                const isSelected = mergeMode && mergeSelected.includes(doc.doc_index);
                return (
                <div key={doc.doc_index} className={`bg-white rounded-xl border p-4 transition-all ${
                  mergeMode ? 'cursor-pointer hover:border-purple-300' : ''
                } ${isSelected ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-200' : 'border-gray-200'}`}
                  onClick={mergeMode ? () => {
                    setMergeSelected(prev =>
                      prev.includes(doc.doc_index)
                        ? prev.filter(x => x !== doc.doc_index)
                        : [...prev, doc.doc_index]
                    );
                  } : undefined}
                >
                  <div className="flex items-start gap-3">
                    {mergeMode ? (
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 border-2 transition-all ${
                        isSelected ? 'bg-purple-600 border-purple-600' : 'bg-white border-gray-300'
                      }`}>
                        {isSelected ? (
                          <Check className="w-4 h-4 text-white" />
                        ) : (
                          <span className="text-sm font-bold text-gray-400">{i + 1}</span>
                        )}
                      </div>
                    ) : (
                      <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-sm font-bold text-blue-600">{i + 1}</span>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      {editingDoc === doc.doc_index ? (
                        <Input
                          data-testid={`doc-title-input-${doc.doc_index}`}
                          value={doc.title}
                          onChange={(e) => updateDocTitle(doc.doc_index, e.target.value)}
                          onBlur={() => setEditingDoc(null)}
                          onKeyDown={(e) => e.key === 'Enter' && setEditingDoc(null)}
                          autoFocus
                          className="rounded-lg text-sm font-medium"
                        />
                      ) : (
                        <div className="flex items-center gap-2 group">
                          <p className="font-medium text-gray-800 truncate">{doc.title}</p>
                          <button
                            data-testid={`edit-title-btn-${doc.doc_index}`}
                            onClick={(e) => { e.stopPropagation(); setEditingDoc(doc.doc_index); }}
                            className="text-gray-300 hover:text-blue-600 transition-colors opacity-0 group-hover:opacity-100 flex-shrink-0"
                            title="Rename"
                          >
                            <PenTool className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                      <p className="text-xs text-gray-400 mt-0.5">
                        {doc.source_files.length} file{doc.source_files.length !== 1 ? 's' : ''} ·{' '}
                        {doc.text ? `${doc.text.length.toLocaleString()} chars` : (
                          <span className="text-amber-600 font-medium">OCR failed — no text extracted</span>
                        )}
                      </p>
                      {doc.text && (
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">{doc.text.slice(0, 150)}...</p>
                      )}
                    </div>
                    {!mergeMode && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); setEditingDoc(doc.doc_index); }}
                        className="h-8 px-2 text-gray-400 hover:text-blue-600 text-xs"
                        data-testid={`rename-btn-${doc.doc_index}`}
                      >
                        <PenTool className="w-3.5 h-3.5 mr-1" /> Rename
                      </Button>
                    )}
                  </div>
                </div>
              )})}
            </div>

            <div className="flex gap-3 pt-2">
              {documents.some(d => !d.text || d.text.length < 50) && (
                <div className="w-full p-3 bg-amber-50 border border-amber-200 rounded-xl mb-2">
                  <p className="text-sm text-amber-800">
                    <AlertCircle className="w-4 h-4 inline mr-1.5" />
                    Some documents have insufficient text. Study dashboards may not generate for those.
                  </p>
                </div>
              )}
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep(0)} className="rounded-xl">
                <ChevronLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              <Button
                data-testid="proceed-to-convert-btn"
                onClick={async () => { await saveDocuments(); setStep(2); }}
                disabled={processingOcr}
                className="flex-1 rounded-xl bg-blue-600 hover:bg-blue-700"
              >
                {processingOcr ? (
                  <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> OCR Processing...</>
                ) : (
                  <>Continue to Convert <ChevronRight className="w-4 h-4 ml-1" /></>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Convert / Pay */}
        {step === 2 && (
          <div className="space-y-5" data-testid="bulk-step-convert">
            <div className="bg-white rounded-2xl border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-800">Turn into Study Dashboards</h2>
                  <p className="text-sm text-gray-500">Each dashboard includes AI summary, key terms, flashcards & quiz</p>
                </div>
              </div>

              {/* Document selection */}
              <div className="space-y-2 mb-5">
                {documents.map((doc, i) => (
                  <label
                    key={doc.doc_index}
                    className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                      doc.selected ? 'border-indigo-300 bg-indigo-50' : 'border-gray-200 bg-gray-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={doc.selected}
                      onChange={() => toggleDocSelection(doc.doc_index)}
                      className="w-4 h-4 accent-indigo-600"
                      data-testid={`select-doc-${doc.doc_index}`}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{doc.title}</p>
                      <p className="text-xs text-gray-400">
                        {doc.text?.length?.toLocaleString() || 0} characters
                        {(doc.source_files?.length || 1) > 1 && ` · ${doc.source_files.length} source files`}
                      </p>
                    </div>
                    {!isPro && doc.selected && (
                      <span className="text-sm font-semibold text-indigo-600">
                        ${pricing?.per_doc?.[documents.filter(d => d.selected).indexOf(doc)]?.toFixed(0) || 0}
                      </span>
                    )}
                  </label>
                ))}
              </div>

              {/* Pricing Summary */}
              <div className={`rounded-xl p-4 ${isPro ? 'bg-amber-50 border border-amber-200' : 'bg-gray-50 border border-gray-200'}`}>
                {isPro ? (
                  <div className="flex items-center gap-2">
                    <Crown className="w-5 h-5 text-amber-600" />
                    <div>
                      <p className="font-semibold text-amber-800">Pro Subscriber</p>
                      <p className="text-sm text-amber-700">
                        All {documents.filter(d => d.selected).length} conversions are free with your Pro plan!
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">
                        {documents.filter(d => d.selected).length} dashboard{documents.filter(d => d.selected).length !== 1 ? 's' : ''}
                        {pricing?.total_source_files > documents.filter(d => d.selected).length && (
                          <span className="text-gray-400"> ({pricing.total_source_files} source files)</span>
                        )}
                      </span>
                      {pricing?.savings > 0 && (
                        <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium">
                          Save ${pricing.savings.toFixed(2)}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-800">Total</span>
                      <div className="text-right">
                        {pricing?.savings > 0 && (
                          <span className="text-xs text-gray-400 line-through mr-2">${pricing?.full_price?.toFixed(2)}</span>
                        )}
                        <span className="text-xl font-bold text-indigo-700">${pricing?.total?.toFixed(2)}</span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-400">
                      1st doc: $3 · Docs 2-5: $2 each · Docs 6+: $1 each
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep(1)} className="rounded-xl">
                <ChevronLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              <Button
                data-testid="convert-btn"
                onClick={handleConvert}
                disabled={documents.filter(d => d.selected).length === 0}
                className="flex-1 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                {isPro ? 'Convert All (Free)' : `Pay $${pricing?.total?.toFixed(2)} & Convert`}
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Processing */}
        {step === 3 && (
          <div className="space-y-6" data-testid="bulk-step-processing">
            <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center">
              <div className="w-16 h-16 bg-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
              </div>
              <h2 className="text-lg font-semibold text-gray-800 mb-2">
                Generating Study Dashboards...
              </h2>
              <p className="text-sm text-gray-500 mb-6">
                Processing {progress.current} of {progress.total} documents
              </p>

              {/* Progress bar */}
              <div className="w-full max-w-md mx-auto h-3 bg-gray-200 rounded-full mb-6 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full transition-all duration-500"
                  style={{ width: `${progress.total > 0 ? (progress.current / progress.total) * 100 : 0}%` }}
                />
              </div>

              {/* Per-doc status */}
              <div className="space-y-2 max-w-md mx-auto text-left">
                {documents.filter(d => d.selected).map((doc) => (
                  <div key={doc.doc_index} className="flex items-center gap-2 text-sm">
                    {doc.conversion_status === 'completed' ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                    ) : doc.conversion_status === 'generating' ? (
                      <Loader2 className="w-4 h-4 text-indigo-500 animate-spin flex-shrink-0" />
                    ) : doc.conversion_status === 'failed' ? (
                      <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                    ) : (
                      <div className="w-4 h-4 rounded-full border-2 border-gray-300 flex-shrink-0" />
                    )}
                    <span className={`truncate ${doc.conversion_status === 'completed' ? 'text-gray-700' : 'text-gray-400'}`}>
                      {doc.title}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Complete */}
        {step === 4 && (
          <div className="space-y-5" data-testid="bulk-step-complete">
            {/* Session-level error banner */}
            {errorReason && (
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3" data-testid="error-reason-banner">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">{errorReason}</p>
                  <p className="text-xs text-amber-600 mt-1">You can try again later without being charged again.</p>
                </div>
              </div>
            )}

            <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center">
              <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 ${
                documents.some(d => d.conversion_status === 'completed') ? 'bg-emerald-100' : 'bg-red-100'
              }`}>
                <GraduationCap className={`w-8 h-8 ${
                  documents.some(d => d.conversion_status === 'completed') ? 'text-emerald-600' : 'text-red-500'
                }`} />
              </div>
              <h2 className="text-xl font-bold text-gray-800 mb-2">
                {documents.some(d => d.conversion_status === 'completed')
                  ? 'Your Study Dashboards are Ready!'
                  : 'Generation Could Not Complete'
                }
              </h2>
              <p className="text-sm text-gray-500 mb-6">
                {documents.filter(d => d.conversion_status === 'completed').length} of {documents.filter(d => d.selected).length} dashboards generated successfully
              </p>
            </div>

            <div className="space-y-3">
              {documents.filter(d => d.selected).map((doc) => (
                <div key={doc.doc_index} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    {doc.conversion_status === 'completed' ? (
                      <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                      </div>
                    ) : (
                      <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                        <AlertCircle className="w-4 h-4 text-red-500" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{doc.title}</p>
                      <p className="text-xs text-gray-400">
                        {doc.conversion_status === 'completed'
                          ? 'Dashboard ready'
                          : (doc.error_reason || 'Generation failed')
                        }
                      </p>
                    </div>
                  </div>
                  {doc.conversion_status === 'completed' && doc.study_doc_id && (
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button
                        data-testid={`add-course-bulk-${doc.doc_index}`}
                        size="sm"
                        variant="outline"
                        onClick={() => setCourseModal({ id: doc.study_doc_id, title: doc.title })}
                        className="rounded-lg border-blue-200 text-blue-700"
                      >
                        <GraduationCap className="w-3.5 h-3.5 mr-1" /> Course
                      </Button>
                      <Button
                        data-testid={`open-dashboard-${doc.doc_index}`}
                        size="sm"
                        onClick={() => navigate(`/study/${doc.study_doc_id}`)}
                        className="rounded-lg bg-blue-600 hover:bg-blue-700"
                      >
                        <BookOpen className="w-3.5 h-3.5 mr-1" /> Open
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Marketplace prompt */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-200 p-5">
              <div className="flex items-start gap-3">
                <ShoppingBag className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-blue-800 mb-1">Sell these notes on DocMarket?</p>
                  <p className="text-sm text-blue-700 mb-3">
                    List your notes on the marketplace and earn money when other students purchase them.
                  </p>
                  <Button
                    data-testid="list-on-marketplace-btn"
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/study-builder')}
                    className="rounded-lg border-blue-300 text-blue-700 hover:bg-blue-100"
                  >
                    Go to Study Builder
                  </Button>
                </div>
              </div>
            </div>

            <Button
              onClick={() => navigate('/study-builder')}
              className="w-full rounded-xl bg-gray-800 hover:bg-gray-900"
            >
              View All Study Dashboards
            </Button>
          </div>
        )}
      </div>

      {/* Add to Course Modal */}
      {courseModal && (
        <AddToCourseModal
          documentId={courseModal.id}
          documentTitle={courseModal.title}
          onClose={() => setCourseModal(null)}
          onSuccess={() => toast.success('Document added to course!')}
        />
      )}

      {/* Merge Confirmation Dialog */}
      {showMergeConfirm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowMergeConfirm(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()} data-testid="merge-confirm-dialog">
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-9 h-9 bg-purple-100 rounded-xl flex items-center justify-center">
                <Merge className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Merge {mergeSelected.length} Documents</h3>
                <p className="text-xs text-gray-500">Combined into one study dashboard</p>
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name for merged document</label>
                <Input
                  data-testid="merge-title-input"
                  value={mergeTitle}
                  onChange={e => setMergeTitle(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleMerge(); }}
                  autoFocus
                  className="rounded-xl"
                  placeholder="e.g. Combined Lecture Notes"
                />
              </div>
              <div className="flex gap-2 pt-1">
                <Button variant="outline" onClick={() => setShowMergeConfirm(false)} className="flex-1 rounded-xl">Cancel</Button>
                <Button
                  data-testid="merge-confirm-btn"
                  onClick={handleMerge}
                  disabled={!mergeTitle.trim()}
                  className="flex-1 rounded-xl bg-purple-600 hover:bg-purple-700"
                >
                  <Merge className="w-4 h-4 mr-1.5" /> Merge
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkUpload;
